import { useEffect, useRef } from 'react';
import * as THREE from 'three';

interface NetworkBackgroundProps {
  className?: string;
}

// Create a circular glow texture
function createGlowTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d')!;
  
  // Create radial gradient for glow effect
  const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  gradient.addColorStop(0, 'rgba(125, 211, 252, 1)');    // sky-300 center
  gradient.addColorStop(0.2, 'rgba(125, 211, 252, 0.8)');
  gradient.addColorStop(0.4, 'rgba(125, 211, 252, 0.4)');
  gradient.addColorStop(0.7, 'rgba(125, 211, 252, 0.1)');
  gradient.addColorStop(1, 'rgba(125, 211, 252, 0)');
  
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 64, 64);
  
  return new THREE.CanvasTexture(canvas);
}

export default function NetworkBackground({ className = '' }: NetworkBackgroundProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const animationFrameRef = useRef<number>(0);

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    // Scene setup
    const scene = new THREE.Scene();
    
    // Camera setup
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    camera.position.z = 50;

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ 
      alpha: true, 
      antialias: true 
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Create glow texture
    const glowTexture = createGlowTexture();

    // Particles
    const particleCount = 100;
    const particles: THREE.Vector3[] = [];
    const velocities: THREE.Vector3[] = [];
    const sizes: number[] = [];
    
    // Create particles with random positions
    for (let i = 0; i < particleCount; i++) {
      const x = (Math.random() - 0.5) * 100;
      const y = (Math.random() - 0.5) * 100;
      const z = (Math.random() - 0.5) * 30;
      particles.push(new THREE.Vector3(x, y, z));
      
      // Random velocity for each particle - slower movement
      velocities.push(new THREE.Vector3(
        (Math.random() - 0.5) * 0.08,
        (Math.random() - 0.5) * 0.08,
        (Math.random() - 0.5) * 0.025
      ));
      
      // Random size for variety
      sizes.push(Math.random() * 2 + 1.5);
    }

    // Create points geometry
    const pointsGeometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    const sizesArray = new Float32Array(particleCount);
    
    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = particles[i].x;
      positions[i * 3 + 1] = particles[i].y;
      positions[i * 3 + 2] = particles[i].z;
      sizesArray[i] = sizes[i];
    }
    
    pointsGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    pointsGeometry.setAttribute('size', new THREE.BufferAttribute(sizesArray, 1));

    // Point material with glow texture
    const pointsMaterial = new THREE.PointsMaterial({
      map: glowTexture,
      color: 0x7dd3fc, // sky-300
      size: 4,
      transparent: true,
      opacity: 0.7,
      sizeAttenuation: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    const points = new THREE.Points(pointsGeometry, pointsMaterial);
    scene.add(points);

    // Lines for connections - using curved lines
    const linesGroup = new THREE.Group();
    scene.add(linesGroup);

    // Animation
    const maxDistance = 25;
    let time = 0;
    
    // Store base positions for wave offset calculation
    const basePositions = particles.map(p => p.clone());
    
    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);
      time += 0.015;

      // Update particle positions
      const positionAttribute = pointsGeometry.getAttribute('position');
      
      for (let i = 0; i < particleCount; i++) {
        // Update base position with velocity
        basePositions[i].x += velocities[i].x;
        basePositions[i].y += velocities[i].y;
        basePositions[i].z += velocities[i].z;
        
        // Bounce off boundaries
        if (Math.abs(basePositions[i].x) > 50) {
          velocities[i].x *= -1;
          basePositions[i].x = Math.sign(basePositions[i].x) * 50;
        }
        if (Math.abs(basePositions[i].y) > 50) {
          velocities[i].y *= -1;
          basePositions[i].y = Math.sign(basePositions[i].y) * 50;
        }
        if (Math.abs(basePositions[i].z) > 15) {
          velocities[i].z *= -1;
          basePositions[i].z = Math.sign(basePositions[i].z) * 15;
        }
        
        // Apply wave motion as offset (not cumulative) - slower
        const waveX = Math.sin(time * 1.0 + i * 0.2) * 2;
        const waveY = Math.cos(time * 0.8 + i * 0.25) * 2;
        
        particles[i].x = basePositions[i].x + waveX;
        particles[i].y = basePositions[i].y + waveY;
        particles[i].z = basePositions[i].z;
        
        positionAttribute.setXYZ(i, particles[i].x, particles[i].y, particles[i].z);
      }
      
      positionAttribute.needsUpdate = true;

      // Update lines - clear and recreate
      while (linesGroup.children.length > 0) {
        const child = linesGroup.children[0];
        if (child instanceof THREE.Line) {
          child.geometry.dispose();
          if (child.material instanceof THREE.Material) {
            child.material.dispose();
          }
        }
        linesGroup.remove(child);
      }

      // Create curved lines between nearby particles
      for (let i = 0; i < particleCount; i++) {
        for (let j = i + 1; j < particleCount; j++) {
          const distance = particles[i].distanceTo(particles[j]);
          
          if (distance < maxDistance) {
            // Create a curved line using QuadraticBezierCurve3
            const midPoint = new THREE.Vector3()
              .addVectors(particles[i], particles[j])
              .multiplyScalar(0.5);
            
            // Add animated curve offset - slower
            const curveOffset = Math.sin(time * 1.3 + i + j) * 2;
            midPoint.z += curveOffset;
            
            const curve = new THREE.QuadraticBezierCurve3(
              particles[i].clone(),
              midPoint,
              particles[j].clone()
            );
            
            const curvePoints = curve.getPoints(10);
            const lineGeometry = new THREE.BufferGeometry().setFromPoints(curvePoints);
            
            // Fade line based on distance with gradient effect
            const opacity = 0.2 * (1 - distance / maxDistance);
            const lineMat = new THREE.LineBasicMaterial({
              color: 0x7dd3fc,
              transparent: true,
              opacity: opacity,
              blending: THREE.AdditiveBlending,
            });
            
            const line = new THREE.Line(lineGeometry, lineMat);
            linesGroup.add(line);
          }
        }
      }

      // Rotation - slower
      scene.rotation.y += 0.0005;
      scene.rotation.x += 0.0002;

      renderer.render(scene, camera);
    };

    animate();

    // Handle resize
    const handleResize = () => {
      if (!containerRef.current) return;
      
      const newWidth = containerRef.current.clientWidth;
      const newHeight = containerRef.current.clientHeight;
      
      camera.aspect = newWidth / newHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(newWidth, newHeight);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameRef.current);
      
      if (rendererRef.current) {
        rendererRef.current.dispose();
        container.removeChild(rendererRef.current.domElement);
      }
      
      pointsGeometry.dispose();
      pointsMaterial.dispose();
      glowTexture.dispose();
    };
  }, []);

  return (
    <div 
      ref={containerRef} 
      className={`absolute inset-0 pointer-events-none ${className}`}
      style={{ zIndex: 0 }}
    />
  );
}
